import { Component, Element, Prop, State, Watch, forceUpdate, h } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import { CreateCaseResponse } from "../../api/publicApi/createCase/createCase";
import { utcFromTime } from "../../utils/utcFromTime";
import { createPhoneLink } from "../../utils/createPhoneLink";

@Component({
    tag: "mf-create-service-case-success",
    styleUrl: "create-service-case-success.css",
    shadow: false
})
export class CreateServiceCaseSuccess {
    @Element() el: HTMLMfCreateServiceCaseSuccessElement;
    /** Response from the API */
    @Prop() response: CreateCaseResponse | null = null;
    /** Click handler for the success button */
    @Prop() clickHandler: () => void;
    /** Locale for the component */
    @Prop() locale: string | null = null;
    /** BaseUrl for MyTrumpf */
    @Prop() myTrumpfBaseUrl: string = "";

    /** used inside a modal */
    @State() isInModal: boolean = false;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.onLocaleChange();
        this.isInModal = this.el?.closest("ux-modal") !== null;
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    render() {
        const t = (key: TranslationKey, options: unknown = undefined) =>
            i18nProvider.t(`mf-service-calls:${String(key)}`, options as Record<string, unknown>);
        const time = (time?: string) => time && <ux-date-time utc={utcFromTime(time)} format={t("timeFormat")}></ux-date-time>;
        const {
            isOutsideOfServiceHours,
            technicalServiceHotline,
            technicalGuideAvailable,
            isCustomerOrderNumberRequired,
            sisCaseId,
            serviceHours
        } = this.response?.[0] || {};
        const hasMonFri = Boolean(serviceHours?.MonFriStart && serviceHours?.MonFriEnd);
        const hasSat = Boolean(serviceHours?.SatStart && serviceHours?.SatEnd);
        const monFriHtml = hasMonFri && (
            <div>
                {t("sisConnector.success.serviceTimes.MonFri")} {time(serviceHours?.MonFriStart)} - {time(serviceHours?.MonFriEnd)}
            </div>
        );
        const satHtml = hasSat && (
            <div>
                {t("sisConnector.success.serviceTimes.Sat")} {time(serviceHours?.SatStart)} - {time(serviceHours?.SatEnd)}
            </div>
        );
        const activeTabOnDetailPage = this.response?.[0]?.technicalGuideAvailable
            ? "activeTabId=sisServiceCallsTabSisProductReports"
            : "activeTabId=serviceCaseTabAiAssistant";

        return (
            <div class="ux-u-text-align--center">
                <div class="ux-u-mb-xxs">
                    <ux-icon style={{ fontSize: "100px" }} class="ux-color-success ux-icon" name="checkmark"></ux-icon>
                </div>
                <div class="ux-text-headline ux-text-headline--big ux-u-mb-xxs">{t("sisConnector.success.headline")}</div>
                {isOutsideOfServiceHours && (
                    <div class="ux-text-normal">
                        <p>{t("sisConnector.success.outsideOfServiceHours")}</p>
                        <strong>
                            {monFriHtml}
                            {satHtml}
                        </strong>
                    </div>
                )}
                {technicalServiceHotline && (
                    <p
                        class="ux-text-normal"
                        innerHTML={t("sisConnector.success.withTechnicalServiceHotline", {
                            hotline: createPhoneLink(technicalServiceHotline, "mf-create-service-case-success__link"),
                            interpolation: { escapeValue: false }
                        })}
                    ></p>
                )}
                {!technicalServiceHotline && (
                    <p class="ux-text-normal">{t("sisConnector.success.withoutTechnicalServiceHotline")}</p>
                )}
                {technicalGuideAvailable && (
                    <div>
                        <div class="ux-text-headline--big">{t("sisConnector.success.technicalGuide.title")}</div>
                        <p class="ux-text-normal">{t("sisConnector.success.technicalGuide.text")}</p>
                    </div>
                )}
                {isCustomerOrderNumberRequired && (
                    <div>
                        <div class="ux-text-headline--big">{t("sisConnector.success.orderNumberRequired.title")}</div>
                        <p class="ux-text-normal">{t("sisConnector.success.orderNumberRequired.text")}</p>
                    </div>
                )}
                <div class="ux-text-normal">
                    <p>
                        <a
                            class="mf-create-service-case-success__link"
                            href={`${this.myTrumpfBaseUrl}/go/mytrumpf/my-service-cases/case/${sisCaseId}/?${activeTabOnDetailPage}`}
                        >
                            {t("sisConnector.success.caseId")}: {sisCaseId}
                        </a>
                    </p>
                </div>
                {!this.isInModal && (
                    <ux-button
                        class="ux-u-margin-top-20"
                        type="button"
                        variant="primary"
                        onClick={() => this.clickHandler?.()}
                        formnovalidate
                    >
                        {t("button.ok")}
                    </ux-button>
                )}
            </div>
        );
    }
}
